import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { ColumnDef } from "@tanstack/react-table";
import { TanStackDataTable } from "../components/TanStackDataTable";
import SimpleMultiSelectFilter from "../components/SimpleMultiSelectFilter";
import { RawDataTable } from "../components/RawDataTable";

interface ByHourProps {
  onLastUpdatedChange?: (lastUpdated: string) => void;
  filterState: any;
  onFilterChange: any;
}

export interface ByHourRef {
  refresh: () => void;
  getLastUpdated: () => string;
}

interface RowData {
  category: string;
  grandTotal: number;
  subRows?: RowData[];
  [key: string]: any; // Dynamic hour bucket columns
}

interface DrillDownData {
  hourBucket: string;
  category: string;
  filterType: string;
  data: any[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
}

const ByHour = forwardRef<ByHourRef, ByHourProps>(
  ({ onLastUpdatedChange }, ref) => {
    const [activeSubTab, setActiveSubTab] = useState(0);
    const [lastUpdated, setLastUpdated] = useState<string>("N/A");
    const [data, setData] = useState<RowData[]>([]);
    const [runtimeDates, setRuntimeDates] = useState<string[]>([]);
    const [selectedRuntimeDate, setSelectedRuntimeDate] = useState<string>("");
    const [timezones, setTimezones] = useState<string[]>([]);
    const [selectedTimezones, setSelectedTimezones] = useState<string[]>([]);
    const [callTypes, setCallTypes] = useState<string[]>([]);
    const [selectedCallTypes, setSelectedCallTypes] = useState<string[]>([]);
    const [planningAreas, setPlanningAreas] = useState<string[]>([]);
    const [selectedPlanningAreas, setSelectedPlanningAreas] = useState<
      string[]
    >([]);
    const [apptDates, setApptDates] = useState<string[]>([]);
    const [selectedApptDates, setSelectedApptDates] = useState<string[]>([]);
    const [ganttTypes] = useState<string[]>(["Jeopardy", "Unresourced"]);
    const [selectedGanttTypes, setSelectedGanttTypes] = useState<string[]>([
      "Jeopardy",
      "Unresourced",
    ]);
    const [hourBuckets, setHourBuckets] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Snapshot table state
    const [snapshotData, setSnapshotData] = useState<Record<string, any>[]>([]);
    const [snapshotColumns, setSnapshotColumns] = useState<string[]>([]);
    const [snapshotHourBuckets, setSnapshotHourBuckets] = useState<string[]>(
      [],
    );
    const [snapshotLoading, setSnapshotLoading] = useState(true);

    // Latest Hour Summary state
    const [profitableData, setProfitableData] = useState<Record<string, any>[]>([]);
    const [nonProfitableData, setNonProfitableData] = useState<Record<string, any>[]>([]);
    const [summaryLoading, setSummaryLoading] = useState(false);

    // Refresh counter to trigger RawDataTable refresh
    const [refreshKey, setRefreshKey] = useState(0);

    // AbortController ref for cancelling previous API calls
    const abortControllerRef = useRef<AbortController | null>(null);

    // Drill-down modal state
    const [drillDownOpen, setDrillDownOpen] = useState(false);
    const [drillDownData, setDrillDownData] = useState<DrillDownData>({
      hourBucket: "",
      category: "",
      filterType: "",
      data: [],
      loading: false,
      total: 0,
      page: 0,
      rowsPerPage: 50,
    });

    // Fetch available runtime dates
    const fetchRuntimeDates = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/runtime-dates");
        const result = await response.json();
        if (result.success && result.runtimeDates) {
          setRuntimeDates(result.runtimeDates);
          // Default to first (most recent) date
          if (result.runtimeDates.length > 0 && !selectedRuntimeDate) {
            setSelectedRuntimeDate(result.runtimeDates[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching runtime dates:", err);
      }
    }, [selectedRuntimeDate]);

    // Fetch available timezones
    const fetchTimezones = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/timezones");
        const result = await response.json();
        if (result.success && result.timezones) {
          setTimezones(result.timezones);
        }
      } catch (err) {
        console.error("Error fetching timezones:", err);
      }
    }, []);

    // Fetch available call types
    const fetchCallTypes = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/call-types");
        const result = await response.json();
        if (result.success && result.callTypes) {
          setCallTypes(result.callTypes);
        }
      } catch (err) {
        console.error("Error fetching call types:", err);
      }
    }, []);

    // Fetch available planning areas
    const fetchPlanningAreas = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/planning-areas");
        const result = await response.json();
        if (result.success && result.planningAreas) {
          setPlanningAreas(result.planningAreas);
        }
      } catch (err) {
        console.error("Error fetching planning areas:", err);
      }
    }, []);

    // Fetch available appointment dates
    const fetchApptDates = useCallback(async () => {
      try {
        const response = await fetch("/api/snowflake/appt-dates");
        const result = await response.json();
        if (result.success && result.apptDates) {
          setApptDates(result.apptDates);
        }
      } catch (err) {
        console.error("Error fetching appointment dates:", err);
      }
    }, []);

    // Fetch snapshot data with optional runtime date and gantt types filter
    const fetchSnapshotData = useCallback(
      async (runtimeDate?: string, ganttTypes?: string[]) => {
        try {
          setSnapshotLoading(true);
          const params = new URLSearchParams();
          if (runtimeDate) params.append("runtimeDate", runtimeDate);
          if (ganttTypes && ganttTypes.length > 0)
            params.append("ganttTypes", ganttTypes.join(","));
          const queryString = params.toString() ? `?${params.toString()}` : "";
          const response = await fetch(`/api/snowflake/snapshot${queryString}`);
          const result = await response.json();
          if (result.success && result.data) {
            setSnapshotData(result.data);
            if (result.columns) {
              setSnapshotColumns(result.columns);
            }
            if (result.hourBuckets) {
              setSnapshotHourBuckets(result.hourBuckets);
            }
          }
        } catch (err) {
          console.error("Error fetching snapshot data:", err);
        } finally {
          setSnapshotLoading(false);
        }
      },
      [],
    );

    // Fetch runtime dates on mount
    useEffect(() => {
      fetchRuntimeDates();
    }, [fetchRuntimeDates]);

    // Fetch filter options on mount
    useEffect(() => {
      fetchTimezones();
      fetchCallTypes();
      fetchPlanningAreas();
      fetchApptDates();
    }, [fetchTimezones, fetchCallTypes, fetchPlanningAreas, fetchApptDates]);

    // Fetch snapshot data when runtime date or gantt types change
    useEffect(() => {
      if (selectedRuntimeDate) {
        fetchSnapshotData(selectedRuntimeDate, selectedGanttTypes);
      }
    }, [selectedRuntimeDate, selectedGanttTypes, fetchSnapshotData]);

    // Fetch Latest Hour Summary data
    const fetchLatestHourSummary = useCallback(async (
      runtimeDate: string,
      tzs: string[],
      cts: string[],
      pas: string[],
      ads: string[],
      gts: string[]
    ) => {
      setSummaryLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("runtimeDate", runtimeDate);
        if (tzs.length > 0) params.append("timezones", tzs.join(","));
        if (cts.length > 0) params.append("callTypes", cts.join(","));
        if (pas.length > 0) params.append("planningAreas", pas.join(","));
        if (ads.length > 0) params.append("apptDates", ads.join(","));
        if (gts.length > 0) params.append("ganttTypes", gts.join(","));
        const queryString = params.toString();

        const [profitableRes, nonProfitableRes] = await Promise.all([
          fetch(`/api/snowflake/latest-hour-summary/profitable?${queryString}`),
          fetch(`/api/snowflake/latest-hour-summary/non-profitable?${queryString}`),
        ]);
        const profitableResult = await profitableRes.json();
        const nonProfitableResult = await nonProfitableRes.json();
        if (profitableResult.success) {
          setProfitableData(profitableResult.data);
        }
        if (nonProfitableResult.success) {
          setNonProfitableData(nonProfitableResult.data);
        }
      } catch (err) {
        console.error("Error fetching latest hour summary:", err);
      } finally {
        setSummaryLoading(false);
      }
    }, []);

    // Fetch Latest Hour Summary when runtime date or filters change
    useEffect(() => {
      if (selectedRuntimeDate) {
        fetchLatestHourSummary(
          selectedRuntimeDate,
          selectedTimezones,
          selectedCallTypes,
          selectedPlanningAreas,
          selectedApptDates,
          selectedGanttTypes
        );
      }
    }, [selectedRuntimeDate, selectedTimezones, selectedCallTypes, selectedPlanningAreas, selectedApptDates, selectedGanttTypes, fetchLatestHourSummary]);

    // Get endpoint based on active tab
    const getEndpoint = useCallback(
      (
        tabIndex: number,
        tzs: string[],
        cts: string[],
        pas: string[],
        ads: string[],
        runtimeDate?: string,
        gts?: string[],
      ) => {
        const params = new URLSearchParams();
        if (runtimeDate) params.append("runtimeDate", runtimeDate);
        if (tzs.length > 0) params.append("timezones", tzs.join(","));
        if (cts.length > 0) params.append("callTypes", cts.join(","));
        if (pas.length > 0) params.append("planningAreas", pas.join(","));
        if (ads.length > 0) params.append("apptDates", ads.join(","));
        if (gts && gts.length > 0) params.append("ganttTypes", gts.join(","));
        const queryString = params.toString() ? `?${params.toString()}` : "";
        switch (tabIndex) {
          case 0:
            return `/api/snowflake/by-hour${queryString}`; // By Jeopardy Reason
          case 1:
            return `/api/snowflake/by-hour/payment-type${queryString}`; // By Payment Type
          case 2:
            return `/api/snowflake/by-hour/planning-area${queryString}`; // By Planning Area
          case 3:
            return `/api/snowflake/by-hour/timezone${queryString}`; // By Timezone
          case 4:
            return `/api/snowflake/by-hour/service-order${queryString}`; // By Service Order
          default:
            return `/api/snowflake/by-hour${queryString}`;
        }
      },
      [],
    );

    // Fetch aggregated data from API
    const fetchData = useCallback(
      async (isRefresh = false) => {
        // Abort previous request if still pending
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
          setError(null);
          if (!isRefresh) {
            setLoading(true);
          }

          const endpoint = getEndpoint(
            activeSubTab,
            selectedTimezones,
            selectedCallTypes,
            selectedPlanningAreas,
            selectedApptDates,
            selectedRuntimeDate,
            selectedGanttTypes,
          );
          const response = await fetch(endpoint, {
            signal: controller.signal,
          });

          // Check if aborted before processing response
          if (controller.signal.aborted) {
            console.log("Request aborted before processing response");
            return;
          }

          const result = await response.json();

          // Check again after parsing JSON
          if (controller.signal.aborted) {
            console.log("Request aborted after parsing response");
            return;
          }

          if (result.success) {
            setData(result.data);
            setHourBuckets(result.hourBuckets || []);
            const currentTime = new Date().toLocaleString();
            setLastUpdated(currentTime);
            if (onLastUpdatedChange) {
              onLastUpdatedChange(currentTime);
            }
          } else {
            setError(result.error || "Failed to fetch data");
          }
          setLoading(false);
        } catch (err: any) {
          // Ignore abort errors - don't update state for aborted requests
          if (err.name === "AbortError") {
            console.log("Request aborted");
            return;
          }
          setError(err.message || "Failed to fetch data");
          console.error("Error fetching by-hour data:", err);
          setLoading(false);
        }
      },
      [
        onLastUpdatedChange,
        activeSubTab,
        getEndpoint,
        selectedTimezones,
        selectedCallTypes,
        selectedPlanningAreas,
        selectedApptDates,
        selectedRuntimeDate,
        selectedGanttTypes,
      ],
    );

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    const handleSubTabChange = (
      _event: React.SyntheticEvent,
      newValue: number,
    ) => {
      setActiveSubTab(newValue);
    };

    // Get filter type based on active tab
    const getFilterType = useCallback((tabIndex: number) => {
      switch (tabIndex) {
        case 0:
          return "jeopardy";
        case 1:
          return "payment-type";
        case 2:
          return "planning-area";
        case 3:
          return "timezone";
        case 4:
          return "service-order";
        default:
          return "jeopardy";
      }
    }, []);

    // Fetch drill-down data with pagination
    const fetchDrillDownData = useCallback(
      async (
        hourBucket: string,
        category: string,
        filterType: string,
        page: number,
        rowsPerPage: number,
        drillDownGanttType?: string | null,
      ) => {
        setDrillDownData((prev) => ({ ...prev, loading: true }));

        // If a specific gantt type is passed from nested row click, use it
        // Otherwise use the selected gantt types filter
        const ganttTypesForDrillDown = drillDownGanttType
          ? [drillDownGanttType]
          : selectedGanttTypes.length > 0
            ? selectedGanttTypes
            : null;

        try {
          const response = await fetch("/api/snowflake/by-hour/drill-down", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              hourBucket,
              category,
              filterType,
              runtimeDate: selectedRuntimeDate || null,
              timezones:
                selectedTimezones.length > 0 ? selectedTimezones : null,
              callTypes:
                selectedCallTypes.length > 0 ? selectedCallTypes : null,
              planningAreas:
                selectedPlanningAreas.length > 0 ? selectedPlanningAreas : null,
              apptDates:
                selectedApptDates.length > 0 ? selectedApptDates : null,
              ganttTypes: ganttTypesForDrillDown,
              page: page + 1, // API uses 1-based pagination
              limit: rowsPerPage,
            }),
          });
          const result = await response.json();

          if (result.success) {
            setDrillDownData((prev) => ({
              ...prev,
              data: result.data,
              total: result.pagination?.total || 0,
              loading: false,
            }));
          } else {
            console.error("Drill-down error:", result.error);
            setDrillDownData((prev) => ({ ...prev, loading: false }));
          }
        } catch (err) {
          console.error("Error fetching drill-down data:", err);
          setDrillDownData((prev) => ({ ...prev, loading: false }));
        }
      },
      [
        selectedRuntimeDate,
        selectedTimezones,
        selectedCallTypes,
        selectedPlanningAreas,
        selectedApptDates,
        selectedGanttTypes,
      ],
    );

    // Handle cell click for drill-down
    const handleCellClick = useCallback(
      (hourBucket: string, category: string, ganttType?: string | null) => {
        if (category === "Grand Total") return; // Don't drill down on grand total

        const filterType = getFilterType(activeSubTab);
        setDrillDownOpen(true);
        setDrillDownData({
          hourBucket,
          category,
          filterType,
          data: [],
          loading: true,
          total: 0,
          page: 0,
          rowsPerPage: 50,
        });

        fetchDrillDownData(hourBucket, category, filterType, 0, 50, ganttType);
      },
      [activeSubTab, getFilterType, fetchDrillDownData],
    );

    // Handle drill-down pagination
    const handleDrillDownPageChange = (_event: unknown, newPage: number) => {
      setDrillDownData((prev) => ({ ...prev, page: newPage }));
      fetchDrillDownData(
        drillDownData.hourBucket,
        drillDownData.category,
        drillDownData.filterType,
        newPage,
        drillDownData.rowsPerPage,
      );
    };

    const handleDrillDownRowsPerPageChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setDrillDownData((prev) => ({
        ...prev,
        rowsPerPage: newRowsPerPage,
        page: 0,
      }));
      fetchDrillDownData(
        drillDownData.hourBucket,
        drillDownData.category,
        drillDownData.filterType,
        0,
        newRowsPerPage,
      );
    };

    // Close drill-down modal
    const handleCloseDrillDown = () => {
      setDrillDownOpen(false);
      setDrillDownData({
        hourBucket: "",
        category: "",
        filterType: "",
        data: [],
        loading: false,
        total: 0,
        page: 0,
        rowsPerPage: 50,
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          fetchData(true);
          setRefreshKey(prev => prev + 1);
          if (selectedRuntimeDate) {
            fetchSnapshotData(selectedRuntimeDate, selectedGanttTypes);
            fetchLatestHourSummary(
              selectedRuntimeDate,
              selectedTimezones,
              selectedCallTypes,
              selectedPlanningAreas,
              selectedApptDates,
              selectedGanttTypes
            );
          }
        },
        getLastUpdated: () => lastUpdated,
      }),
      [fetchData, lastUpdated, selectedRuntimeDate, selectedTimezones, selectedCallTypes, selectedPlanningAreas, selectedApptDates, selectedGanttTypes, fetchSnapshotData, fetchLatestHourSummary],
    );

    // Dynamically generate column definitions based on hour buckets from API
    const columns = useMemo<ColumnDef<RowData>[]>(() => {
      const cols: ColumnDef<RowData>[] = [];

      // For By Service Order tab (activeSubTab === 4), add ganttType as first column
      if (activeSubTab === 4) {
        cols.push({
          accessorKey: "ganttType",
          header: "Type",
          size: 100,
          enableSorting: true,
        });
      }

      cols.push({
        accessorKey: "category",
        header: activeSubTab === 4 ? "Service Order" : "",
        size: activeSubTab === 4 ? 180 : 200,
        enableSorting: true,
      });

      // Add dynamic hour bucket columns with clickable cells
      hourBuckets.forEach((hour) => {
        cols.push({
          accessorKey: hour,
          header: hour,
          size: 55,
          cell: ({ getValue, row }) => {
            const value = getValue<number>();
            const category = row.original.category;
            const isGrandTotal = category === "Grand Total";

            // For nested rows, get the parent category
            // row.depth > 0 means it's a child row
            const parentRow = row.getParentRow();
            const parentCategory = parentRow?.original?.category;
            const grandParentRow = parentRow?.getParentRow?.();
            const grandParentCategory = grandParentRow?.original?.category;

            // Determine the effective category and gantt type for drill-down
            // Different tabs have different nesting structures:
            // - By Reason (tab 0): GANTT_TYPE -> JEOPARDY_GROUP -> JEOPARDY_REASON (3 levels)
            // - Other tabs: CATEGORY -> GANTT_TYPE (2 levels)
            let effectiveCategory = category;
            let ganttType: string | null = null;

            if (activeSubTab === 0) {
              // By Reason tab: 3-level structure
              // Level 0: GANTT_TYPE (Jeopardy/Unresourced) - use as gantt type filter only
              // Level 1: JEOPARDY_GROUP - use as category, parent is gantt type
              // Level 2: JEOPARDY_REASON - use as category, grandparent is gantt type
              if (grandParentCategory) {
                // Level 2: reason under group under gantt type
                effectiveCategory = category;
                ganttType = grandParentCategory;
              } else if (parentCategory) {
                // Level 1: group under gantt type
                effectiveCategory = category;
                ganttType = parentCategory;
              } else {
                // Level 0: gantt type itself - filter by gantt type only
                effectiveCategory = category;
                ganttType = category;
              }
            } else {
              // Other tabs: 2-level structure (CATEGORY -> GANTT_TYPE)
              if (parentCategory) {
                // Child row: gantt type under parent category
                effectiveCategory = parentCategory;
                ganttType = category;
              }
            }

            if (value === undefined || value === null || value === 0) {
              return "";
            }

            return (
              <Box
                component="span"
                onClick={() =>
                  !isGrandTotal &&
                  handleCellClick(hour, effectiveCategory, ganttType)
                }
                sx={{
                  cursor: isGrandTotal ? "default" : "pointer",
                  color: isGrandTotal ? "inherit" : "#1976d2",
                  "&:hover": {
                    textDecoration: isGrandTotal ? "none" : "underline",
                  },
                }}
              >
                {value.toLocaleString()}
              </Box>
            );
          },
        });
      });

      return cols;
    }, [hourBuckets, handleCellClick, activeSubTab]);

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: { xs: 2, md: 1.75 },
          bgcolor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* Runtime Date Filter and Gantt Type Filter */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiInputBase-root": { fontSize: "0.8rem", height: 32 },
              "& .MuiInputLabel-root": { fontSize: "0.8rem" },
            }}
          >
            <InputLabel id="runtime-date-filter-label">Run Date</InputLabel>
            <Select
              labelId="runtime-date-filter-label"
              id="runtime-date-filter"
              value={selectedRuntimeDate}
              label="Run Date"
              onChange={(e) => setSelectedRuntimeDate(e.target.value as string)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    "& .MuiMenuItem-root": { fontSize: "0.8rem", py: 0.25 },
                  },
                },
              }}
            >
              {runtimeDates.map((rd) => (
                <MenuItem key={rd} value={rd} sx={{ fontSize: "0.8rem" }}>
                  {rd}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <SimpleMultiSelectFilter
            label="Gantt Type"
            options={ganttTypes}
            selectedValues={selectedGanttTypes}
            onChange={setSelectedGanttTypes}
          />
        </Box>

        {/* Snapshot Table */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Summary By Progression
          </Typography>
          {snapshotLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : snapshotData.length > 0 ? (
            <TanStackDataTable
              key={`snapshot-table-${refreshKey}`}
              data={snapshotData}
              columns={[
                {
                  accessorKey: "category",
                  header: "",
                  size: 250,
                },
                ...snapshotHourBuckets.map((hour) => ({
                  accessorKey: hour,
                  header: hour,
                  size: 60,
                  cell: ({ getValue }: { getValue: () => number }) => {
                    const value = getValue();
                    if (value === undefined || value === null || value === 0) {
                      return "";
                    }
                    return value.toLocaleString();
                  },
                })),
              ]}
              getSubRows={(row) => row.subRows}
              maxHeight="180px"
              defaultExpanded={false}
              enableVirtualization={false}
              rowHeight={28}
              pinFirstColumn={true}
            />
          ) : null}
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <SimpleMultiSelectFilter
            label="Timezone"
            options={timezones}
            selectedValues={selectedTimezones}
            onChange={setSelectedTimezones}
            maxWidth={180}
          />
          <SimpleMultiSelectFilter
            label="Call Type"
            options={callTypes}
            selectedValues={selectedCallTypes}
            onChange={setSelectedCallTypes}
            maxWidth={180}
          />
          <SimpleMultiSelectFilter
            label="Planning Area"
            options={planningAreas}
            selectedValues={selectedPlanningAreas}
            onChange={setSelectedPlanningAreas}
            showSearch
            maxWidth={300}
          />
          <SimpleMultiSelectFilter
            label="Appt Date"
            options={apptDates}
            selectedValues={selectedApptDates}
            onChange={setSelectedApptDates}
            maxWidth={180}
          />
        </Box>

        {/* Sub-tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeSubTab}
            onChange={handleSubTabChange}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="By Reason" />
            <Tab label="By Payment Type" />
            <Tab label="By Planning Area" />
            <Tab label="By Timezone" />
            <Tab label="By Service Order" />
          </Tabs>
        </Box>

        {/* TanStack DataTable with expandable rows */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TanStackDataTable
            key={`main-table-${refreshKey}`}
            data={data}
            columns={columns}
            getSubRows={(row) => row.subRows}
            maxHeight="400px"
            defaultExpanded={false}
            enableVirtualization={true}
            rowHeight={22}
            pinFirstColumn={true}
            enableSorting={true}
            sortableColumns={["category"]}
          />
        )}

        {/* Latest Hour Summary Section */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
            Latest Hour Summary
          </Typography>
          {summaryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 3 }}>
              {/* Profitable Data Table */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                  Profitable Data
                </Typography>
                <TanStackDataTable
                  data={profitableData}
                  columns={[
                    { accessorKey: "PAYMENT_METHOD", header: "Call Type", size: 80 },
                    { accessorKey: "RN", header: "RN", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "AT", header: "AT", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "NH", header: "NH", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "WS", header: "WS", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "WP", header: "WP", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "O", header: "O", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CV", header: "CV", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CP", header: "CP", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CA", header: "CA", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "ED", header: "ED", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CO", header: "CO", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "TOTAL", header: "Total", size: 60, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                  ]}
                  maxHeight="220px"
                  enableVirtualization={false}
                  pinFirstColumn={true}
                />
              </Box>
              {/* Non-Profitable Data Table */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                  Non-Profitable Data
                </Typography>
                <TanStackDataTable
                  data={nonProfitableData}
                  columns={[
                    { accessorKey: "PAYMENT_METHOD", header: "Call Type", size: 80 },
                    { accessorKey: "RN", header: "RN", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "AT", header: "AT", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "NH", header: "NH", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "WS", header: "WS", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "WP", header: "WP", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "O", header: "O", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CV", header: "CV", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CP", header: "CP", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CA", header: "CA", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "ED", header: "ED", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "CO", header: "CO", size: 50, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                    { accessorKey: "TOTAL", header: "Total", size: 60, cell: ({ getValue }: { getValue: () => number }) => getValue()?.toLocaleString() || "" },
                  ]}
                  maxHeight="260px"
                  enableVirtualization={false}
                  pinFirstColumn={true}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Raw Data Table - Extracted to separate component */}
        <RawDataTable
          key={refreshKey}
          columnWidths={{ ORDERNUM: 110 }}
          runtimeDate={selectedRuntimeDate}
          timezones={selectedTimezones}
          callTypes={selectedCallTypes}
          planningAreas={selectedPlanningAreas}
          apptDates={selectedApptDates}
          ganttTypes={selectedGanttTypes}
        />

        {/* Drill-down Modal */}
        <Dialog
          open={drillDownOpen}
          onClose={handleCloseDrillDown}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: "90vw",
              maxWidth: "1400px",
              maxHeight: "95vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #e2e8f0",
              py: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 600 }}
              >
                Raw Data: {drillDownData.category}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{ ml: 2, color: "#64748b" }}
              >
                {selectedRuntimeDate} | Hour: {drillDownData.hourBucket} |{" "}
                {drillDownData.total.toLocaleString()} records
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDrillDown} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              paddingTop: "4px !important",
              pb: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <RawDataTable
              externalData={drillDownData.data}
              externalLoading={drillDownData.loading}
              externalTotal={drillDownData.total}
              drillDownFilters={{
                hourBucket: drillDownData.hourBucket,
                category: drillDownData.category,
                filterType: drillDownData.filterType,
              }}
              title=""
              showDownload={true}
              showPagination={false}
              columnWidths={{ ORDERNUM: 110 }}
              maxHeight="calc(95vh - 180px)"
              runtimeDate={selectedRuntimeDate}
              timezones={selectedTimezones}
              callTypes={selectedCallTypes}
              planningAreas={selectedPlanningAreas}
              apptDates={selectedApptDates}
            />
            <TablePagination
              component="div"
              count={drillDownData.total}
              page={drillDownData.page}
              onPageChange={handleDrillDownPageChange}
              rowsPerPage={drillDownData.rowsPerPage}
              onRowsPerPageChange={handleDrillDownRowsPerPageChange}
              rowsPerPageOptions={[25, 50, 100, 200]}
              sx={{
                borderTop: "1px solid #e2e8f0",
                bgcolor: "#f8fafc",
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  minHeight: 36,
                  height: 36,
                  overflow: "hidden",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontSize: "0.75rem",
                  },
                "& .MuiTablePagination-select": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </DialogContent>
        </Dialog>
      </Box>
    );
  },
);

ByHour.displayName = "ByHour";

export default ByHour;
